import React, { useRef, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { LabelTemplate, TEMPLATE_CATEGORIES } from '@/types/label';
import { 
  FileText, 
  Trash2, 
  Copy, 
  Download, 
  Plus,
  Clock,
  Globe,
  Upload,
  Filter,
  Send,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TemplateManagerProps {
  templates: LabelTemplate[];
  globalTemplates: LabelTemplate[];
  currentTemplateId?: string;
  onSelectTemplate: (template: LabelTemplate) => void;
  onDeleteTemplate?: (id: string) => void;
  onDuplicateTemplate: (template: LabelTemplate) => void;
  onExportTemplate: (template: LabelTemplate) => void;
  onNewTemplate: () => void;
  onImportTemplate: (file: File, isGlobal: boolean) => void;
  canDelete?: boolean;
  canManageOwn?: boolean;
  canDuplicate?: boolean;
  canManageCategories?: boolean;
  canSubmitForReview?: boolean;
  onUpdateCategory?: (templateId: string, category: string | undefined) => void;
  onSubmitForReview?: (template: LabelTemplate) => void;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
  templates,
  globalTemplates,
  currentTemplateId,
  onSelectTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
  onExportTemplate,
  onNewTemplate,
  onImportTemplate,
  canDelete = true,
  canManageOwn = true,
  canDuplicate = true,
  canManageCategories = false,
  canSubmitForReview = false,
  onUpdateCategory,
  onSubmitForReview,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImportTemplate(file, false);
      e.target.value = '';
    }
  };

  const filteredTemplates = useMemo(() => {
    if (categoryFilter === 'all') return templates;
    if (categoryFilter === 'uncategorized') return templates.filter(t => !t.category);
    return templates.filter(t => t.category === categoryFilter);
  }, [templates, categoryFilter]);

  const filteredGlobalTemplates = useMemo(() => {
    if (categoryFilter === 'all') return globalTemplates;
    if (categoryFilter === 'uncategorized') return globalTemplates.filter(t => !t.category);
    return globalTemplates.filter(t => t.category === categoryFilter);
  }, [globalTemplates, categoryFilter]);

  const usedCategories = useMemo(() => {
    const cats = new Set<string>();
    [...templates, ...globalTemplates].forEach(t => {
      if (t.category) cats.add(t.category);
    });
    return Array.from(cats).sort();
  }, [templates, globalTemplates]);

  const renderTemplateCard = (template: LabelTemplate) => (
    <Card 
      key={template.id}
      className={`cursor-pointer transition-colors hover:border-primary ${
        currentTemplateId === template.id ? 'border-primary bg-primary/5' : ''
      }`}
      onClick={() => onSelectTemplate(template)}
    >
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="truncate flex items-center gap-1.5">
            {template.isGlobal && <Globe className="w-3 h-3 text-primary shrink-0" />}
            {template.name}
          </span>
          <span className="text-xs font-normal text-muted-foreground flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            {formatDistanceToNow(template.updatedAt, { addSuffix: true })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">
              {template.elements.length} element{template.elements.length !== 1 ? 's' : ''}
            </span>
            {template.category && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                {template.category}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {canDuplicate && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDuplicateTemplate(template);
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onExportTemplate(template);
              }}
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
            {canManageCategories && onUpdateCategory && (
              <Select
                value={template.category || '__none__'}
                onValueChange={(v) => {
                  onUpdateCategory(template.id, v === '__none__' ? undefined : v);
                }}
              >
                <SelectTrigger 
                  className="h-7 w-7 p-0 border-0 bg-transparent [&>svg]:hidden"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Category</SelectItem>
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Submit for review - only for user's own non-global templates */}
            {!template.isGlobal && canSubmitForReview && onSubmitForReview && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-primary"
                title="Submit for global review"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSubmitForReview(template);
                }}
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* Only show delete for user's own templates and if permission allows */}
            {!template.isGlobal && canDelete && onDeleteTemplate && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDeleteTemplate(template.id);
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Category Filter */}
      {usedCategories.length > 0 && (
        <div className="px-4 pt-3 pb-1">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-7 text-xs">
              <Filter className="w-3 h-3 mr-1" />
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              {usedCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Global Templates Section */}
          {filteredGlobalTemplates.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="w-3 h-3 text-primary" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Global Templates
                </span>
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {filteredGlobalTemplates.length}
                </Badge>
              </div>
              
              <div className="space-y-2">
                {filteredGlobalTemplates.map((template) => renderTemplateCard(template))}
              </div>
            </div>
          )}

          {/* User Templates Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  My Templates
                </span>
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {filteredTemplates.length}
                </Badge>
              </div>
              {canManageOwn && (
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleImportClick} 
                    className="h-6 px-2 text-xs gap-1"
                  >
                    <Upload className="w-3 h-3" />
                    Import
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onNewTemplate} 
                    className="h-6 px-2 text-xs gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    New
                  </Button>
                </div>
              )}
            </div>
            
            {filteredTemplates.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 border border-dashed rounded-lg">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No saved templates</p>
                <p className="text-xs mt-1">Save your current template to see it here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map((template) => renderTemplateCard(template))}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
